import { useEffect, useMemo, useState } from "react";
import { catcat as cc } from "cerulean";
import { entities, modules, dollars } from "byzantine";
import { useLocalization } from "@fluent/react";
import {
  ContentCard,
  Checkbox,
  Radio,
  Row,
  useBreakpoints,
} from "@narmi/design_system";
import Filters from "byzantine/src/filters";
import { InstitutionFeatures } from "byzantine/src/types";
import { BulkPaymentRowType } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import styles from "./CreateBulk.module.scss";

const calculateInstantOrSameDayFees = (
  payments: BulkPaymentRowType[],
  instantFees: API.InstantFeesResponse | null,
  sameDayFees: API.InstantFeesResponse | null
) => {
  let totalFees = 0;

  const perPaymentFees = payments.map((payment) => {
    const { amount, speedStatusWithExpediteOption } = payment;

    // if there is no speed or no amount, there is no fee
    if (!speedStatusWithExpediteOption || !amount) {
      return 0;
    }

    // convert amount from cents to dollars
    const amountDollars = amount / 100;
    let feeConfig: API.InstantFeesResponse | null = null;

    if (speedStatusWithExpediteOption === "instant") {
      feeConfig = instantFees ?? null;
    } else if (speedStatusWithExpediteOption === "sameDay") {
      feeConfig = sameDayFees ?? null;
    }

    // if we don't have the required config for this particular payment, skip it
    if (!feeConfig) {
      return 0;
    }
    let fee = 0;

    switch (feeConfig.kind) {
      case "free":
        fee = 0;
        break;

      case "fixed":
        // if fixed, just return max
        fee = feeConfig.max ?? 0;
        break;

      case "percent": {
        if (!feeConfig.percent) return 0;

        const raw = amountDollars * feeConfig.percent;

        if (feeConfig.max !== undefined && raw > feeConfig.max) {
          fee = feeConfig.max;
        } else if (feeConfig.min !== undefined && raw < feeConfig.min) {
          fee = feeConfig.min;
        } else {
          fee = raw;
        }
        break;
      }

      default:
        fee = 0;
    }

    totalFees += fee;
    return fee;
  });

  return {
    perPaymentFees,
    totalFees,
  };
};

const ExpeditePaymentsCard = ({
  expeditableCount,
}: {
  expeditableCount: number;
}) => {
  const { l10n } = useLocalization();
  const features = useUserFeatures() as InstitutionFeatures;
  const { s } = useBreakpoints();
  const form = entities.bulkPayments.BulkPaymentForm.useForm();
  const { getInstantOrSameDayFees } =
    modules.transfers.useGetInstantOrSameDayFees();
  const [instantFeeConfig, setInstantFeeConfig] =
    useState<API.InstantFeesResponse | null>(null);
  const [sameDayFeeConfig, setSameDayFeeConfig] =
    useState<API.InstantFeesResponse | null>(null);
  const {
    same_day_ach: sameDayFeature,
    fednow_send: fedNowFeature,
    expedited_payments: expeditedPaymentsFeature,
  } = features;
  const { chosenExpediteOption, payments } = form.values;
  const sameDayOptionOnly = sameDayFeature && !fedNowFeature;
  const fedNowOptionOnly = fedNowFeature && !sameDayFeature;
  const displayAllOptionsInCard = sameDayFeature && fedNowFeature;

  const totalFees = useMemo(() => {
    if (!chosenExpediteOption) return 0;
    if (!instantFeeConfig && !sameDayFeeConfig) return 0;

    const { totalFees: totalCalculatedFees } = calculateInstantOrSameDayFees(
      payments,
      instantFeeConfig,
      sameDayFeeConfig
    );
    return totalCalculatedFees;
  }, [payments, instantFeeConfig, sameDayFeeConfig, chosenExpediteOption]);

  const fednowableCount = useMemo(
    () => payments.filter((p) => p.speedStatus === "instant").length,
    [payments]
  );

  useEffect(() => {
    const instantFees = getInstantOrSameDayFees("instant");
    const sameDayFees = getInstantOrSameDayFees("sameDay");

    Promise.all([instantFees, sameDayFees])
      .then(([instantData, sameDayData]) => {
        setInstantFeeConfig(instantData);
        setSameDayFeeConfig(sameDayData);
      })
      .catch((error) => {
        console.error("Error fetching fees data:", error);
      });
  }, []);

  useEffect(() => {
    if (!displayAllOptionsInCard) return;

    const isFastestSelected = chosenExpediteOption === "fastest";

    // edge case where user has added payments that are fednowable
    // then expedites by selecting "fastest" option, and then removes those fednowable payments
    // we dont want to leave the "fastest" option checked, but fallback to same-day if available
    if (isFastestSelected && fednowableCount === 0 && sameDayFeature) {
      form.setFieldValue("chosenExpediteOption", "sameDay");
    }
  }, [
    chosenExpediteOption,
    fednowableCount,
    sameDayFeature,
    displayAllOptionsInCard,
    form,
  ]);

  useEffect(() => {
    if (chosenExpediteOption) {
      form.setFieldValue("totalExpediteFees", totalFees);
    } else {
      form.setFieldValue("totalExpediteFees", 0);
    }
  }, [totalFees, chosenExpediteOption]);

  const handleToggleCard = () => {
    let optionToSet = "";

    if (sameDayOptionOnly) {
      optionToSet = "sameDay";
    } else if (fedNowOptionOnly) {
      optionToSet = "fedNow";
      // user is enabling the card and both options are available; default to
      // "fastest" when any payment is fednow-eligible, otherwise fall back to
      // same-day (the "fastest" option is hidden when nothing is eligible)
    } else if (displayAllOptionsInCard) {
      optionToSet = fednowableCount > 0 ? "fastest" : "sameDay";
    }

    // user is toggling the card so we set default option
    if (optionToSet && !chosenExpediteOption) {
      form.setFieldValue("chosenExpediteOption", optionToSet);
      // user is toggling off card
    } else if (chosenExpediteOption) {
      form.setFieldValue("chosenExpediteOption", "");
    }
  };

  const handleCheckOption = (val: string) => {
    form.setFieldValue("chosenExpediteOption", val);
  };

  const FastestMethodCard = () =>
    displayAllOptionsInCard && fednowableCount > 0 ? (
      <div className="margin--top--l">
        <Radio
          kind="input-card"
          name="overview"
          value="fastest"
          checked={chosenExpediteOption === "fastest"}
          onCheck={handleCheckOption}
        >
          <h4 className="fontFamily--body fontSize--m fontWeight--semibold">
            {l10n.getString("fastest-method-option")}
            <span className="narmi-icon-zap margin--left--xs" />
          </h4>
          <p className="fontSize--s margin--top--xxs fontColor--primary">
            {l10n.getString("fastest-method-description")}
          </p>
        </Radio>
      </div>
    ) : null;

  const SameDayCard = () => {
    const SameDayCardContent = () => (
      <>
        <h4 className="fontFamily--body fontSize--m fontWeight--semibold">
          {l10n.getString("same-day-option")}
          <span className="narmi-icon-clock margin--left--xs" />
        </h4>
        <p className="fontSize--s margin--top--xxs fontColor--primary">
          {l10n.getString("same-day-description")}
        </p>
      </>
    );

    return (
      <>
        {displayAllOptionsInCard ? (
          <div className="margin--top--l">
            <Radio
              kind="input-card"
              name="overview"
              value="sameDay"
              checked={chosenExpediteOption === "sameDay"}
              onCheck={handleCheckOption}
            >
              <SameDayCardContent />
            </Radio>
          </div>
        ) : null}
        {sameDayOptionOnly ? (
          <div className="margin--top--l">
            <ContentCard>
              <SameDayCardContent />
            </ContentCard>
          </div>
        ) : null}
      </>
    );
  };

  const InstantCard = () =>
    fedNowOptionOnly ? (
      <div className="margin--top--l">
        <ContentCard>
          <h4 className="fontFamily--body fontSize--m fontWeight--semibold">
            {l10n.getString("label-instant")}
            <span className="narmi-icon-zap margin--left--xs" />
          </h4>
          <p className="fontSize--s margin--top--xxs fontColor--primary">
            {l10n.getString("instant-description")}
          </p>
        </ContentCard>
      </div>
    ) : null;

  const TotalFeesCard = () => (
    <div className={cc("margin--top--l", styles.totalFeesCard)}>
      <Row alignItems="center">
        <Row.Item>
          <h4 className="fontFamily--body fontSize--s fontWeight--semibold">
            <Row alignItems="center" gapSize="xs">
              <Row.Item shrink>
                {l10n.getString("total-expedite-fees-label")}
              </Row.Item>
              <Row.Item shrink>
                <span className="narmi-icon-dollar-bill-line fontSize--l" />
              </Row.Item>
            </Row>
          </h4>
        </Row.Item>
        <Row.Item shrink>
          <h4 className="fontFamily--body fontSize--m fontWeight--semibold">
            {Filters.currency(dollars(totalFees), {
              hasDecimal: true,
            })}
          </h4>
          <p className="fontSize--xs margin--top--xxs">
            {l10n.getString("in-total-fees-label")}
          </p>
        </Row.Item>
      </Row>
    </div>
  );

  return (
    <>
      {expeditedPaymentsFeature ? (
        <div className={cc("margin--top--l", styles.expediteCard)}>
          <ContentCard>
            <Row gapSize="xs" alignItems="center">
              <Row.Item {...(s ? { shrink: true } : {})}>
                <Checkbox
                  label={l10n.getString("expedite-card-cta")}
                  name="expedite-card"
                  checked={Boolean(chosenExpediteOption)}
                  onChange={handleToggleCard}
                />
              </Row.Item>
              <Row.Item shrink>
                <div className={styles.totalExpeditePaymentsHighlight}>
                  {expeditableCount}
                </div>
              </Row.Item>
            </Row>
            {chosenExpediteOption ? (
              <>
                <FastestMethodCard />
                <SameDayCard />
                <InstantCard />
                {totalFees > 0 ? <TotalFeesCard /> : null}
              </>
            ) : null}
          </ContentCard>
        </div>
      ) : null}
    </>
  );
};

export default ExpeditePaymentsCard;
